import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, ClipboardList, Calendar, Gavel, Building2, ListChecks, ShieldAlert, FileDown } from "lucide-react";
import { toast } from "sonner";

import ReviewerScoringPanel from "@/components/scoring/ReviewerScoringPanel";
import DecisionPanel from "@/components/scoring/DecisionPanel";
import CompliancePanel from "@/components/scoring/CompliancePanel";
import SiteVisitReportPanel from "@/components/scoring/SiteVisitReportPanel";

export default function SchoolApplicationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data: app } = await api.get(`/schools/applications/${id}`);
      setData(app);
      if (user.role === "admin") {
        try {
          const { data: users } = await api.get("/admin/users");
          setReviewers(users.filter((u) => u.role === "site_visitor" || u.role === "reviewer"));
        } catch { /* non-admin */ }
      }
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [id, user.role]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !data) return <div className="text-sm text-slate-500">Loading…</div>;
  const isStaff = user.role === "admin" || user.role === "reviewer";

  return (
    <div className="animate-fade-up" data-testid="school-app-detail">
      <Link to="/dashboard/schools" className="inline-flex items-center text-sm text-slate-600 hover:text-primary mb-4">
        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to school applications
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-primary text-white">ACIS</Badge>
            <Badge variant="secondary" className="bg-slate-100 text-slate-700 capitalize">{data.institution_type}</Badge>
            <StatusBadge status={data.status} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-3">
            <Building2 className="h-6 w-6" /> {data.name}
          </h1>
          <div className="text-sm text-slate-500">{[data.city, data.state, data.country].filter(Boolean).join(", ")} · {data.contact_email}</div>
        </div>
        {data.accredited_school?.accreditation_number && (
          <Link
            to={`/certificate/school/${data.accredited_school.accreditation_number}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="school-download-cert"
          >
            <Button variant="outline" className="border-primary/40 text-primary hover:bg-primary/5">
              <FileDown className="h-4 w-4 mr-2" /> Download accreditation certificate
            </Button>
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="bg-slate-100 p-1 flex flex-wrap">
                <TabsTrigger value="overview" data-testid="sa-tab-overview"><ClipboardList className="h-3.5 w-3.5 mr-1.5" />Overview</TabsTrigger>
                <TabsTrigger value="scoring" data-testid="sa-tab-scoring"><ListChecks className="h-3.5 w-3.5 mr-1.5" />Reviewer Scoring</TabsTrigger>
                <TabsTrigger value="reviews" data-testid="sa-tab-reviews">Recommendations</TabsTrigger>
                <TabsTrigger value="visits" data-testid="sa-tab-visits"><Calendar className="h-3.5 w-3.5 mr-1.5" />Site visits</TabsTrigger>
                <TabsTrigger value="compliance" data-testid="sa-tab-compliance"><ShieldAlert className="h-3.5 w-3.5 mr-1.5" />Compliance</TabsTrigger>
                <TabsTrigger value="decisions" data-testid="sa-tab-decisions"><Gavel className="h-3.5 w-3.5 mr-1.5" />Decisions</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6 space-y-5">
                <Field label="Programs offered">
                  {data.program_offerings?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {data.program_offerings.map((p) => <Badge key={p} variant="secondary" className="bg-slate-100">{p}</Badge>)}
                    </div>
                  ) : "—"}
                </Field>
                <Field label="Website">{data.website || "—"}</Field>
                <Field label="Student capacity">{data.student_capacity || "—"}</Field>
                <Field label="Self-study summary">
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{data.self_study_summary}</p>
                </Field>
              </TabsContent>

              <TabsContent value="scoring" className="mt-6 space-y-4">
                {isStaff ? (
                  <ReviewerScoringPanel applicationId={data.id} />
                ) : (
                  <Card className="p-6 text-sm text-slate-500 border-slate-200">
                    Reviewer scoring is restricted to assigned reviewers and administrators.
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="reviews" className="mt-6 space-y-4">
                {data.reviews?.length === 0 && <div className="text-sm text-slate-500">No legacy reviews yet.</div>}
                {data.reviews?.map((r) => (
                  <Card key={r.id} className="p-4 border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-primary">{r.reviewer_email}</div>
                      <Badge variant="outline" className="capitalize">{r.recommendation.replace(/_/g, " ")}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mb-2">{new Date(r.created_at).toLocaleString()}</div>
                    <p className="text-sm text-slate-700">{r.summary}</p>
                    {r.standards_concerns?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Concerns</div>
                        <ul className="text-xs text-slate-700 list-disc pl-5">
                          {r.standards_concerns.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                  </Card>
                ))}
                {(user.role === "reviewer" || user.role === "admin") && (
                  <ReviewForm appId={data.id} onCreated={reload} />
                )}
              </TabsContent>

              <TabsContent value="visits" className="mt-6 space-y-4">
                <SiteVisitReportPanel
                  applicationId={data.id}
                  reviewers={reviewers}
                  onChange={reload}
                />
              </TabsContent>

              <TabsContent value="compliance" className="mt-6 space-y-4">
                <CompliancePanel
                  applicationId={data.id}
                  siteVisits={data.site_visits || []}
                />
              </TabsContent>

              <TabsContent value="decisions" className="mt-6 space-y-4">
                {isStaff && <DecisionPanel applicationId={data.id} accreditationType={data.accreditation_type || "institutional"} />}
                <div className="border-t pt-4 mt-4">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Recorded accreditation decisions</div>
                  {data.decisions?.length === 0 && <div className="text-sm text-slate-500">No decisions recorded.</div>}
                {data.decisions?.map((d) => (
                  <Card key={d.id} className="p-4 border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-emerald-100 text-emerald-800 capitalize">{d.decision}</Badge>
                      <div className="text-xs text-slate-500">{d.decided_by} · {new Date(d.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-sm text-slate-700 leading-relaxed">Valid for {d.valid_years} years.</div>
                    {d.conditions && <div className="text-xs text-slate-600 mt-1">Conditions: {d.conditions}</div>}
                    {d.rationale && <div className="text-xs text-slate-600 mt-1">Rationale: {d.rationale}</div>}
                  </Card>
                ))}
                {user.role === "admin" && (data.status === "site_visit_complete" || data.status === "decision_pending" || data.status === "under_review") && (
                  <DecisionForm appId={data.id} onCreated={reload} />
                )}
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        <div className="space-y-6">
          {isStaff && (
            <Card className="p-6 border-slate-200">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Reviewer actions</div>
              <ChangeStatusForm appId={data.id} current={data.status} onChange={reload} />
            </Card>
          )}
          <Card className="p-6 border-slate-200">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">History</div>
            <ol className="text-xs space-y-2">
              {(data.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5" />
                  <div>
                    <div className="text-slate-700 capitalize font-medium">{(h.status || "").replace(/_/g, " ")}</div>
                    <div className="text-slate-500">{new Date(h.at).toLocaleString()} · {h.by}</div>
                    {h.notes && <div className="italic text-slate-600">"{h.notes}"</div>}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">{label}</div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    submitted: "bg-blue-50 text-blue-800 border-blue-200",
    under_review: "bg-amber-50 text-amber-800 border-amber-200",
    site_visit_scheduled: "bg-violet-50 text-violet-800 border-violet-200",
    site_visit_complete: "bg-indigo-50 text-indigo-800 border-indigo-200",
    decision_pending: "bg-orange-50 text-orange-800 border-orange-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    denied: "bg-red-50 text-red-800 border-red-200",
  };
  return <Badge variant="outline" className={`capitalize ${map[status] || map.draft}`}>{(status || "").replace(/_/g, " ")}</Badge>;
}

function ChangeStatusForm({ appId, current, onChange }) {
  const [status, setStatus] = useState(current);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/schools/applications/${appId}/status`, { status, notes });
      toast.success("Status updated");
      onChange();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1.5 block text-xs">Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="sa-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="under_review">Under review</SelectItem>
            <SelectItem value="site_visit_scheduled">Site visit scheduled</SelectItem>
            <SelectItem value="site_visit_complete">Site visit complete</SelectItem>
            <SelectItem value="decision_pending">Decision pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="denied">Denied</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="mb-1.5 block text-xs">Notes</Label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sa-status-notes" />
      </div>
      <Button onClick={submit} disabled={busy} className="w-full bg-primary text-white hover:bg-[#113A64]" data-testid="sa-status-save">
        {busy ? "Saving…" : "Save status"}
      </Button>
    </div>
  );
}

function ReviewForm({ appId, onCreated }) {
  const [rec, setRec] = useState("approve");
  const [summary, setSummary] = useState("");
  const [concerns, setConcerns] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!summary) { toast.error("Summary required"); return; }
    setBusy(true);
    try {
      await api.post("/accreditation/reviews", {
        school_application_id: appId,
        recommendation: rec,
        summary,
        standards_concerns: concerns.split("\n").map((s) => s.trim()).filter(Boolean),
        standards_met: [],
      });
      toast.success("Review recorded");
      setSummary(""); setConcerns("");
      onCreated();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-5 border-slate-200 bg-slate-50">
      <h3 className="font-heading text-sm font-semibold text-primary mb-3">Submit an accreditation review</h3>
      <div className="space-y-3">
        <Select value={rec} onValueChange={setRec}>
          <SelectTrigger data-testid="rev-rec-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="approve">Approve</SelectItem>
            <SelectItem value="approve_with_conditions">Approve with conditions</SelectItem>
            <SelectItem value="site_visit">Site visit required</SelectItem>
            <SelectItem value="deny">Deny</SelectItem>
          </SelectContent>
        </Select>
        <Textarea rows={3} placeholder="Summary" value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="rev-summary" />
        <Textarea rows={2} placeholder="Standards concerns (one per line)" value={concerns} onChange={(e) => setConcerns(e.target.value)} data-testid="rev-concerns" />
        <Button onClick={submit} disabled={busy} className="bg-primary text-white hover:bg-[#113A64]" data-testid="rev-submit">{busy ? "Saving…" : "Submit review"}</Button>
      </div>
    </Card>
  );
}

function ScheduleVisitForm({ appId, reviewers, onCreated }) {
  const [date, setDate] = useState("");
  const [agenda, setAgenda] = useState("");
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!date) { toast.error("Date required"); return; }
    setBusy(true);
    try {
      await api.post("/site-visits", {
        school_application_id: appId,
        scheduled_date: date,
        agenda,
        visitor_user_ids: selected ? [selected] : [],
      });
      toast.success("Site visit scheduled");
      setDate(""); setAgenda(""); setSelected("");
      onCreated();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-5 border-slate-200 bg-slate-50">
      <h3 className="font-heading text-sm font-semibold text-primary mb-3">Schedule a site visit</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="mb-1.5 block text-xs">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sv-date" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Assign site visitor</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="sv-visitor"><SelectValue placeholder="Choose user" /></SelectTrigger>
            <SelectContent>
              {reviewers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.role}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-xs">Agenda</Label>
          <Textarea rows={2} value={agenda} onChange={(e) => setAgenda(e.target.value)} data-testid="sv-agenda" />
        </div>
      </div>
      <div className="mt-3"><Button onClick={submit} disabled={busy} className="bg-primary text-white hover:bg-[#113A64]" data-testid="sv-submit">{busy ? "Scheduling…" : "Schedule visit"}</Button></div>
    </Card>
  );
}

function DecisionForm({ appId, onCreated }) {
  const [decision, setDecision] = useState("accredited");
  const [years, setYears] = useState(5);
  const [conditions, setConditions] = useState("");
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/accreditation/decisions", {
        school_application_id: appId,
        decision,
        valid_years: Number(years),
        conditions,
        rationale,
      });
      toast.success("Decision recorded");
      setConditions(""); setRationale("");
      onCreated();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-5 border-slate-200 bg-emerald-50/40">
      <h3 className="font-heading text-sm font-semibold text-primary mb-3">Issue accreditation decision</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="mb-1.5 block text-xs">Decision</Label>
          <Select value={decision} onValueChange={setDecision}>
            <SelectTrigger data-testid="dec-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="accredited">Accredited</SelectItem>
              <SelectItem value="probation">Probation</SelectItem>
              <SelectItem value="denied">Denied</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Validity (years)</Label>
          <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} data-testid="dec-years" />
        </div>
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-xs">Conditions</Label>
          <Textarea rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)} data-testid="dec-conditions" />
        </div>
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-xs">Rationale</Label>
          <Textarea rows={2} value={rationale} onChange={(e) => setRationale(e.target.value)} data-testid="dec-rationale" />
        </div>
      </div>
      <div className="mt-3"><Button onClick={submit} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="dec-submit">{busy ? "Saving…" : "Issue decision"}</Button></div>
    </Card>
  );
}
